import React, { useMemo, useState } from 'react';
import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from '@headlessui/react';
import {
  AlertTriangle,
  ArrowRight,
  CheckCircle2,
  Clock3,
  Film,
  ImageOff,
  List,
  Star,
  X,
} from 'lucide-react';
import { projectTownStatus } from './maps';

function projectName(project) {
  return project?.name || project?.title || '未命名项目';
}

function projectTime(project) {
  const value = project?.updatedAt || project?.createdAt;
  const time = value ? new Date(value).getTime() : 0;
  return Number.isFinite(time) ? time : 0;
}

function projectDeadline(project) {
  const date = String(project?.deadline || '').slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return '';
  const parsed = new Date(`${date}T12:00:00`);
  if (!Number.isFinite(parsed.getTime())) return '';
  const [year, month, day] = date.split('-').map(Number);
  return parsed.getFullYear() === year && parsed.getMonth() + 1 === month && parsed.getDate() === day ? date : '';
}

export default function TownBoardPanel({ projects = [], onClose, onOpenProject, onSwitchToList }) {
  const [activeFilter, setActiveFilter] = useState('all');

  const data = useMemo(() => {
    const rows = projects.map((project) => ({ project, status: projectTownStatus(project) }));
    const stats = {
      production: rows.filter((item) => item.status.key === 'production').length,
      overdue: rows.filter((item) => item.status.key === 'overdue').length,
      delivered: rows.filter((item) => item.status.key === 'delivered').length,
      coverless: projects.filter((project) => !project?.coverImage).length,
    };
    const priorities = [...rows].sort((left, right) => {
      const rank = { overdue: 0, production: 1, delivered: 2 };
      const statusDiff = rank[left.status.key] - rank[right.status.key];
      if (statusDiff !== 0) return statusDiff;
      if (Boolean(left.project?.favorite) !== Boolean(right.project?.favorite)) {
        return left.project?.favorite ? -1 : 1;
      }
      return projectTime(right.project) - projectTime(left.project);
    });
    let headline = '小镇今天很安静，可以从一部正在制作的项目开始。';
    if (stats.overdue > 0) headline = `有 ${stats.overdue} 部项目已超过计划日期，先收住最关键的一步。`;
    else if (stats.production > 0) headline = `有 ${stats.production} 部项目正在制作，继续最近的进度最顺手。`;
    else if (stats.delivered > 0) headline = `当前项目都已交付，可以回看成片或筹备下一部作品。`;
    return { stats, priorities, headline };
  }, [projects]);

  const today = new Intl.DateTimeFormat('zh-CN', {
    month: 'long',
    day: 'numeric',
    weekday: 'short',
  }).format(new Date());

  const statItems = [
    { key: 'all', label: '全部', value: projects.length, icon: List },
    { key: 'production', label: '制作中', value: data.stats.production, icon: Film },
    { key: 'overdue', label: '已延期', value: data.stats.overdue, icon: Clock3 },
    { key: 'delivered', label: '已交付', value: data.stats.delivered, icon: CheckCircle2 },
    { key: 'coverless', label: '待补封面', value: data.stats.coverless, icon: ImageOff },
  ];
  const visibleProjects = data.priorities.filter(({ project, status }) => (
    activeFilter === 'all'
      || (activeFilter === 'coverless' ? !project.coverImage : status.key === activeFilter)
  ));
  const activeFilterLabel = statItems.find((item) => item.key === activeFilter)?.label || '全部';

  return (
    <Dialog open onClose={() => onClose?.()} className="pixel-town-drawer-root">
      <DialogBackdrop className="pixel-town-drawer-scrim" />

      <DialogPanel as="aside" className="pixel-town-drawer">
        <header className="pixel-town-drawer__header">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <DialogTitle className="text-base font-semibold">今日片场</DialogTitle>
              <span className="text-xs" style={{ color: 'var(--color-text-muted)' }}>{today}</span>
            </div>
            <p className="pixel-town-board__headline">{data.headline}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="pixel-town-icon-button shrink-0"
            title="关闭"
            aria-label="关闭"
          >
            <X size={16} />
          </button>
        </header>

        <section className="pixel-town-board__filters" aria-label="按项目状态筛选">
          {statItems.map((item) => {
            const Icon = item.icon;
            return (
              <button
                type="button"
                key={item.key}
                className="pixel-town-board__filter"
                aria-pressed={activeFilter === item.key}
                onClick={() => setActiveFilter(item.key)}
              >
                <Icon size={13} />
                <span>{item.label}</span>
                <span className="pixel-town-board__filter-count">{item.value}</span>
              </button>
            );
          })}
        </section>

        <section className="flex-1 min-h-0 overflow-y-auto px-4 py-4">
          <div className="mb-3 flex items-center justify-between">
            <div>
              <h3 className="text-sm font-semibold">{activeFilter === 'all' ? '项目动线' : activeFilterLabel}</h3>
              <p className="mt-0.5 text-xs" style={{ color: 'var(--color-text-muted)' }}>延期优先，其余按收藏和最近时间排列</p>
            </div>
            <span className="font-mono-num text-xs" aria-live="polite" style={{ color: 'var(--color-text-muted)' }}>{visibleProjects.length} 部</span>
          </div>

          {visibleProjects.length === 0 ? (
            <div className="min-h-56 flex flex-col items-center justify-center border border-dashed rounded-md" style={{ borderColor: 'var(--color-border-primary)' }}>
              <Film size={28} style={{ color: 'var(--color-text-muted)' }} />
              <div className="mt-3 text-sm font-medium">{projects.length === 0 ? '小镇还没有项目' : `没有「${activeFilterLabel}」项目`}</div>
              <button
                type="button"
                onClick={projects.length === 0 ? onSwitchToList : () => setActiveFilter('all')}
                className="pixel-town-button pixel-town-button--primary mt-4"
              >
                {projects.length === 0 ? '去项目列表' : '查看全部项目'} <ArrowRight size={13} />
              </button>
            </div>
          ) : (
            <div className="space-y-2">
              {visibleProjects.map(({ project, status }) => {
                const deadline = projectDeadline(project);
                const StatusIcon = status.key === 'overdue' ? AlertTriangle : status.key === 'delivered' ? CheckCircle2 : Film;
                const statusColor = status.key === 'overdue'
                  ? 'var(--color-error)'
                  : status.key === 'delivered'
                    ? 'var(--color-success)'
                    : 'var(--color-accent-primary)';
                return (
                  <button
                    type="button"
                    key={project.id}
                    onClick={() => onOpenProject?.(project)}
                    className="pixel-town-project-row"
                  >
                    <div className="h-10 w-10 shrink-0 rounded overflow-hidden flex items-center justify-center" style={{ backgroundColor: 'var(--color-bg-tertiary)' }}>
                      {project.coverImage ? (
                        <img src={project.coverImage} alt="" className="w-full h-full object-cover" loading="lazy" />
                      ) : (
                        <StatusIcon size={18} style={{ color: statusColor }} />
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5">
                        <span className="text-sm font-medium truncate" title={projectName(project)}>{projectName(project)}</span>
                        {project.favorite && <Star size={12} fill="currentColor" style={{ color: 'var(--color-accent-primary)' }} />}
                      </div>
                      <div className="mt-1 flex items-center gap-2 text-2xs" style={{ color: 'var(--color-text-muted)' }}>
                        <span className="inline-flex items-center gap-1" style={{ color: statusColor }}><StatusIcon size={11} />{status.label}</span>
                        {!project.coverImage && <span>待补封面</span>}
                      </div>
                      {deadline && (
                        <time className="pixel-town-board__deadline" dateTime={deadline}>计划截止 {deadline.replaceAll('-', '.')}</time>
                      )}
                    </div>
                    <ArrowRight size={15} style={{ color: 'var(--color-text-muted)' }} />
                  </button>
                );
              })}
            </div>
          )}
        </section>

        <footer className="shrink-0 p-4 border-t" style={{ borderColor: 'var(--color-border-secondary)' }}>
          <p className="pixel-town-board__status-note">状态来自项目资料与计划日期，不代表生成任务进度。</p>
          <button
            type="button"
            onClick={onSwitchToList}
            className="pixel-town-button w-full"
          >
            <List size={14} /> 查看全部项目
          </button>
        </footer>
      </DialogPanel>
    </Dialog>
  );
}
